import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import SaviBusinessJsonLd from "../components/SaviBusinessJsonLd";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import SaviQuiz from "../components/SaviQuiz";
import AlumniWall from "../components/AlumniWall";
import WaitlistSignup from "../components/WaitlistSignup";
import OfficialAwardBadge from "../components/OfficialAwardBadge";
import { MobileFold } from "../components/home/MobileFold";
import { ROOMS, TAX_NOTE, WHATSAPP, LAST_MINUTE, formatPrice } from "../data/rooms";
import { pic, picSrc } from "../lib/pic";
import { useWide } from "../lib/useWide";
import {
  Wifi, Users, Mountain, Home, MessageCircle, Mic, ArrowRight, Sun, Coffee, Dumbbell, Moon,
  Monitor, Waves, UtensilsCrossed, TreePine, Flower2, PawPrint, CalendarDays, Bell, ShoppingBasket
} from "lucide-react";

const trackEvent = (action, label) => {
  if (window.gtag) {
    window.gtag('event', action, { event_category: 'conversion', event_label: label });
  }
  if (window.fbq) {
    window.fbq('track', action === 'apply_click' ? 'Lead' : 'ViewContent', { content_name: label });
  }
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const PhotoBreak = ({ name, alt, eyebrow, title, position = "center", testid }) => (
  <section className="relative h-[46vh] sm:h-[58vh] overflow-hidden" data-testid={testid}>
    <img {...pic(name)} alt={alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: position }} />
    <div className="absolute inset-0 bg-gradient-to-t from-[#2E2A26]/90 via-[#2E2A26]/30 to-black/10" />
    <div className="absolute inset-x-0 bottom-0 p-6 sm:p-12 lg:p-16">
      <div className="max-w-5xl mx-auto">
        <p className="text-[#E8DAC8]/80 font-label text-xs tracking-[0.25em] uppercase mb-3" style={{textShadow: '0 2px 8px rgba(0,0,0,0.5)'}}>{eyebrow}</p>
        <h2 className="font-serif text-3xl sm:text-5xl text-white leading-[1.05] max-w-3xl" style={{textShadow: '0 2px 14px rgba(0,0,0,0.5)'}}>{title}</h2>
      </div>
    </div>
  </section>
);

const Eyebrow = ({ children, light }) => (
  <p className={`font-label text-xs tracking-[0.2em] uppercase text-center mb-2 ${light ? "text-[#C89B7B]" : "text-[#C85A38]"}`}>{children}</p>
);

const HomePage = () => {
  const wide = useWide();
  // Support shareable savicoliving.com/#quiz and /#dates links (SPA doesn't auto-scroll to hash)
  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (id === "quiz" || id === "dates") setTimeout(() => scrollTo(id), 400);
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Savi Coliving | Europe's Award-Winning Sober Coliving near Valencia</title>
        <meta name="description" content="Europe's award-winning sober coliving. Best for alcohol-free living, deep work and long stays in the mountains near Valencia. Winner, Best Destination Coliving 2026. 16 rooms, pool, coworking, shared dinners, 25 minutes from Valencia. Application only." />
        <meta property="og:title" content="Savi Coliving | Europe's Award-Winning Sober Coliving near Valencia" />
        <meta property="og:description" content="Best for alcohol-free living, deep work and long stays in the mountains near Valencia. Winner, Best Destination Coliving 2026." />
        <link rel="canonical" href="https://www.savicoliving.com" />
      </Helmet>
      <SaviBusinessJsonLd id="home" />

      {/* Navigation */}
      <PublicNav />

      {/* Mobile Bottom Navigation */}
      <PublicBottomNav />

      {/* HERO */}
      <section className="pt-16">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              {...pic("hero-villa")}
              alt=""
              fetchpriority="high"
              className="sm:hidden w-full h-full object-cover"
              data-testid="hero-poster-mobile"
            />
            {wide && (
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                poster={picSrc("hero-villa", 960)}
              >
                <source src="https://video.wixstatic.com/video/f539fd_dd46caed36484dc99c7b55ef39a38ba2/1080p/mp4/file.mp4" type="video/mp4" />
              </video>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#2E2A26] via-[#2E2A26]/65 to-[#2E2A26]/50" />
          </div>

          {/* Content sits in normal flow so the hero grows on short phones instead of hiding the pill under the header */}
          <div className="relative min-h-[calc(100svh-8rem)] sm:min-h-screen flex flex-col items-center justify-center text-center px-6 pt-2 pb-14 sm:py-16">
            <a
              href={LAST_MINUTE.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('last_minute_click', 'hero_pill')}
              className="inline-flex items-center gap-2.5 bg-[#C85A38]/85 hover:bg-[#C85A38] backdrop-blur-md border border-white/15 rounded-full pl-3.5 pr-5 py-2 mt-6 sm:mt-8 mb-6 sm:mb-8 shadow-lg shadow-[#C85A38]/25 transition-colors max-w-[90vw]"
              data-testid="urgency-badge"
            >
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              <span className="text-white text-[12px] sm:text-[11px] normal-case tracking-normal sm:uppercase sm:tracking-[0.14em] text-center leading-relaxed">
                <span className="font-semibold">Last minute:</span> {LAST_MINUTE.line} · <span className="underline underline-offset-4 decoration-white/50">Book now</span>
              </span>
            </a>

            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white mb-5 max-w-5xl leading-[1.05] tracking-tight" style={{textShadow: '0 2px 12px rgba(0,0,0,0.6), 0 12px 40px rgba(0,0,0,0.4)'}} data-testid="hero-headline">
              You become<br /><span className="italic">your environment.</span>
            </h1>

            <p className="text-white/70 text-base sm:text-lg tracking-wide mb-6" style={{textShadow: '0 2px 10px rgba(0,0,0,0.7)', letterSpacing: '0.03em'}} data-testid="hero-secondary">
              A villa community in the mountains of Valencia. Sober by design.
            </p>

            <p className="text-white/55 text-sm sm:text-base mb-8 sm:mb-10 tracking-wide max-w-xl" style={{textShadow: '0 2px 8px rgba(0,0,0,0.6)'}}>
              Wake up clear. Work well. Eat together. Leave with friends you&apos;ll actually keep.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3 mb-6">
              <button onClick={() => { scrollTo('dates'); trackEvent('apply_click', 'hero'); }} data-testid="hero-cta" className="bg-[#C85A38] hover:bg-[#B34E2E] text-white rounded-full px-12 py-6 text-base font-medium tracking-wide shadow-lg shadow-[#C85A38]/20 transition-all duration-300 hover:shadow-xl hover:shadow-[#C85A38]/30 hover:scale-[1.02]">
                Check dates &amp; rooms
              </button>
              <a
                href={`${WHATSAPP}?text=${encodeURIComponent("Hi Steve! I've got a question about staying at Savi.")}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', 'hero')}
                className="inline-flex items-center gap-2 text-white/85 hover:text-white border border-white/30 hover:border-white/60 rounded-full px-8 py-4 text-sm tracking-wide backdrop-blur-sm transition-colors"
                data-testid="hero-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> Ask Steve a question
              </a>
            </div>

            <p className="text-white/45 text-xs tracking-wider" style={{letterSpacing: '0.08em', textShadow: '0 1px 6px rgba(0,0,0,0.5)'}} data-testid="hero-microcopy">
              Steve replies personally, usually within 24 hours.
            </p>

            <div className="mt-8 lg:mt-0 lg:absolute lg:top-8 lg:right-8 xl:right-14">
              <OfficialAwardBadge testid="hero-award-badge" width={260} className="hidden lg:inline-block" />
              <OfficialAwardBadge testid="hero-award-badge-mobile" width={200} className="lg:hidden" />
            </div>
          </div>

          <div className="hidden sm:block absolute bottom-10 left-1/2 -translate-x-1/2">
            <div className="w-px h-14 bg-gradient-to-b from-white/20 to-transparent" />
          </div>
        </div>
      </section>

      {/* ============ 1. THE PLACE ============ */}
      <section className="py-12 px-4 bg-[#FBF7F1]" data-testid="section-place">
        <div className="max-w-2xl mx-auto text-center">
          <Eyebrow>The place</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] leading-snug mb-4">
            A 16-room villa in the Sierra Calderona,<br className="hidden sm:block" /> 25 minutes from Valencia.
          </h2>
          <p className="text-[#2E2A26]/60 text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
            Pool, a proper coworking room, a 200 m² sky terrace looking out to the sea, three resident dogs, and a long table in the courtyard where dinner tends to run late. Twelve to sixteen people live here at a time, for a month or more — and nobody drinks.
          </p>
        </div>
      </section>

      {/* PHOTO GRID */}
      <section className="px-2 sm:px-4 bg-[#FBF7F1] py-1">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-2">
          <img loading="lazy" decoding="async" {...pic("villa-front", { sizes: "(min-width: 768px) 25vw, 50vw", fallback: 480 })} alt="Savi villa" className="w-full h-48 sm:h-56 object-cover rounded-lg" />
          <img loading="lazy" decoding="async" {...pic("dogs", { sizes: "(min-width: 768px) 25vw, 50vw", fallback: 480 })} alt="The resident dogs at Savi Coliving on a sierra trail" className="w-full h-48 sm:h-56 object-cover rounded-lg" />
          <img loading="lazy" decoding="async" {...pic("dinner-v2", { sizes: "(min-width: 768px) 25vw, 50vw", fallback: 480 })} alt="Guests sharing dinner in the courtyard at Savi Coliving" className="w-full h-48 sm:h-56 object-cover rounded-lg" style={{objectPosition: 'center 62%'}} />
          <img loading="lazy" decoding="async" {...pic("pool", { sizes: "(min-width: 768px) 25vw, 50vw", fallback: 480 })} alt="The pool at Savi Coliving, seen through the arched veranda" className="w-full h-48 sm:h-56 object-cover rounded-lg" />
        </div>
      </section>

      {/* SKY TERRACE */}
      <section className="relative mt-6">
        <div className="relative h-[60vh] sm:h-[75vh] overflow-hidden" data-testid="sky-room-view-section">
          <img
            {...pic("sky-terrace")}
            loading="lazy" decoding="async"
            alt="View from the Sky Rooms' 200sqm terrace at Savi Coliving — pine forest, Valencia, and the Mediterranean"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/15 to-black/35" />
          <div className="absolute top-6 right-6 sm:top-10 sm:right-10">
            <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-white" />
              <span className="text-white text-[11px] sm:text-xs tracking-[0.2em] uppercase">From the Sky Rooms</span>
            </div>
          </div>
          <div className="absolute inset-0 flex items-start p-6 sm:p-12 lg:p-16 pt-16 sm:pt-20 lg:pt-24">
            <div className="max-w-5xl mx-auto w-full">
              <p className="text-white/70 text-xs sm:text-sm tracking-[0.25em] uppercase mb-3" style={{textShadow: '0 2px 8px rgba(0,0,0,0.6)'}}>200 m² Sky Terrace</p>
              <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl text-white leading-[1.05] max-w-3xl mb-4" style={{textShadow: '0 2px 14px rgba(0,0,0,0.55)'}}>
                Your office, until you look up.
              </h2>
              <p className="text-white/80 text-sm sm:text-base max-w-xl mb-6" style={{textShadow: '0 2px 10px rgba(0,0,0,0.6)'}}>
                The Sky Rooms open directly onto a 200 m² terrace overlooking the pine forest, the city of Valencia, and the Mediterranean.
              </p>
              <Link to="/rooms#sky">
                <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white/20 transition-colors text-white text-xs sm:text-sm tracking-wider uppercase rounded-full px-5 py-3">
                  See the Sky Rooms <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* COWORKING */}
      <section className="py-12 px-4 bg-[#FBF7F1]">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <img loading="lazy" decoding="async"
              {...pic("coworking", { sizes: "(min-width: 768px) 448px, 100vw", fallback: 768 })}
              alt="Coworking space at Savi with dual screens and mountain views"
              className="w-full h-64 sm:h-80 object-cover rounded-xl"
            />
            <div>
              <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase mb-2">Where the work happens</p>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-3">A real workspace, not a kitchen table</h2>
              <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
                Dual screens at every desk. Starlink WiFi up to 500Mbps. Floor-to-ceiling windows onto the mountains. Quiet by default — no music, no calls at the desks — so the people around you are working too.
              </p>
              <div className="space-y-2 mb-4">
                {["Ergonomic chairs & standing desks", "Weekly skill shares — one guest teaches, everyone learns", "Quiet, focused room from morning to mid-afternoon"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-[#C85A38]" />
                    <p className="text-[#2E2A26]/50 text-xs">{item}</p>
                  </div>
                ))}
              </div>
              <Link to="/coworking-valencia" className="inline-block py-2 text-[#C85A38] text-xs font-medium hover:underline">
                More about the workspace →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 2. THE PEOPLE ============ */}
      <section className="py-12 px-4 bg-[#E8DAC8]" data-testid="section-people">
        <div className="max-w-3xl mx-auto">
          <Eyebrow>The people · a note from Steve</Eyebrow>
          <div className="flex flex-col md:flex-row gap-8 items-start mt-5">
            <img loading="lazy" decoding="async"
              {...pic("steve", { sizes: "144px", fallback: 480 })}
              alt="Steve, founder of Savi Coliving"
              className="w-28 h-28 md:w-36 md:h-36 rounded-2xl object-cover flex-shrink-0 mx-auto md:mx-0"
              data-testid="steve-photo"
            />
            <div className="relative">
              <div className="absolute -top-4 -left-2 text-[#C85A38]/15 text-7xl font-serif leading-none select-none">"</div>
              <blockquote className="relative pl-6 sm:pl-8">
                <p className="font-serif text-lg sm:text-xl text-[#2E2A26]/80 leading-relaxed mb-5">
                  I spent twenty years in hospitality building places where strangers became mates by midnight — and every one of them ran on drinks. When I went remote, I ate dinner alone in a beautiful place for months. So I built the house I wanted to live in.
                </p>
                <p className="text-[#2E2A26]/60 text-base leading-relaxed mb-5">
                  Savi is simple. Comfortable rooms, a good workspace, a long table, and a small group of people who stay long enough to actually know each other. No alcohol, which turns out to be the thing that makes the rest work. Marina and I live here too, with the dogs.
                </p>
                <p className="text-[#2E2A26] text-base leading-relaxed font-medium italic">
                  If it sounds like your kind of place, send us your dates. We&apos;d love to meet you.
                </p>
                <p className="text-[#2E2A26]/50 text-sm mt-3">— Steve, founder</p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <Eyebrow>What guests say</Eyebrow>
          <p className="text-[#2E2A26]/40 text-xs text-center mb-6">Verified Google reviews from Savi residents</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "A rare environment where you can slow down, connect, and leave clearer, more focused, and recharged than when you arrived. When I'm back in Spain, this is where I'll come home to.", name: "Travis Weeks", role: "Resident", location: "90-day stay", img: "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/tc416uth_travis.jpg" },
              { quote: "I've come across many co-working and co-living spaces through my work — Savi, with its sober concept, is truly something very special. A place that leaves a lasting impression.", name: "Saskia Rehhahn", role: "Hospitality professional", location: "", img: "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/wr0ogbx9_saskia.jpeg" },
              { quote: "I genuinely couldn't have found a better co-living spot in Valencia. The location is incredible. The community is warm. The standard of hosting is on another level.", name: "Devinn", role: "Resident", location: "Google Local Guide", img: "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/ovbdty6h_devvo.jpg" }
            ].map((t, i) => (
              <div key={i} className="bg-[#FBF7F1] rounded-2xl p-6 border border-[#E8DAC8]">
                <p className="text-[#2E2A26]/70 text-sm leading-relaxed mb-5">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <img loading="lazy" decoding="async" src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-medium text-[#2E2A26] text-sm">{t.name}</p>
                    <p className="text-[#2E2A26]/40 text-xs">{t.role}{t.location ? `, ${t.location}` : ""}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-2xl mx-auto mt-10" data-testid="who-you-meet">
            {[
              { stat: "up to 16", label: "people in the house" },
              { stat: "1–3 months", label: "typical stay" },
              { stat: "0", label: "drinks, by design" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <p className="text-[#C85A38] text-2xl sm:text-3xl font-serif mb-1">{item.stat}</p>
                <p className="text-[#2E2A26]/50 text-xs">{item.label}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#2E2A26]/50 text-sm max-w-xl mx-auto mt-6">
            Founders, developers, writers, consultants — some years sober, many sober-curious, most just done organising their social life around a bar. We read every enquiry so the house stays a good mix.
          </p>
        </div>
      </section>

      <AlumniWall />

      {/* ============ 3. EVERYDAY LIFE ============ */}
      <section className="py-14 px-4 bg-[#2E2A26]" data-testid="section-everyday">
        <div className="max-w-5xl mx-auto">
          <Eyebrow light>Everyday life</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl text-white mb-8 text-center">A normal Tuesday</h2>
          <MobileFold id="everyday" dark title="The day, and the week" teaser="Coffee at sunrise, twelve laptops in one room, dinner that finishes when it finishes. Plus the Mon–Sun rhythm." cta="See it">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { icon: Sun, time: "Morning", desc: "Someone's made coffee for whoever comes down next. Sunrise over the sierra. A quiet start." },
              { icon: Coffee, time: "Midday", desc: "Twelve laptops in one room, not a single hangover. Lunch on the terrace, phones face down." },
              { icon: Dumbbell, time: "Afternoon", desc: "Gym, a hike from the door, the pool. Space to think and move." },
              { icon: Moon, time: "Evening", desc: "Dinner at the long table in the courtyard. It finishes when it finishes. Nobody leaves early." }
            ].map((item, i) => (
              <div key={i} className="bg-[#FBF7F1] rounded-xl p-5">
                <item.icon className="w-5 h-5 text-[#C85A38] mb-2" />
                <p className="text-[#C85A38] text-xs font-medium mb-1.5">{item.time}</p>
                <p className="text-[#2E2A26]/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto" data-testid="weekly-rhythm">
            <p className="text-[#E8DAC8]/70 text-sm text-center mb-4">
              The week has a gentle rhythm. Nothing&apos;s compulsory — most people come anyway.
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                { day: "Mon", title: "Intentions", desc: "Say what you're working on this week, over coffee." },
                { day: "Tue", title: "Skill share", desc: "One guest teaches what they're best at." },
                { day: "Wed", title: "Move", desc: "Sierra hike, run club, yoga, padel." },
                { day: "Thu", title: "Family dinner", desc: "Cooked together, eaten in the courtyard." },
                { day: "Fri", title: "Check-in", desc: "How did the week go? Celebrate what shipped." },
                { day: "Sat", title: "Adventure", desc: "Beach, day trips, games night." },
                { day: "Sun", title: "Reset", desc: "Pool, meditation, quiet time." }
              ].map((item) => (
                <div key={item.day} className="flex items-center gap-4 bg-[#FBF7F1] rounded-xl px-4 py-3" data-testid={`rhythm-${item.day.toLowerCase()}`}>
                  <span className="w-9 flex-shrink-0 text-[#C85A38] font-label text-xs tracking-widest uppercase">{item.day}</span>
                  <div>
                    <p className="text-[#2E2A26] text-sm font-medium">{item.title}</p>
                    <p className="text-[#2E2A26]/50 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </MobileFold>
        </div>
      </section>

      {/* THE SAVI APP */}
      <section className="py-12 px-4 bg-[#FBF7F1]" data-testid="app-showcase">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase mb-3">The Savi App</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-4">The house runs on its own app.</h2>
            <p className="text-[#2E2A26]/55 text-sm leading-relaxed mb-7">We built it ourselves, for this house. From the day you book, your stay is organised — quietly, in your pocket.</p>
            <div className="space-y-4">
              {[
                { icon: CalendarDays, label: "See what's on", detail: "Yoga, dinners, hikes, lifts to town — one tap to say you're in" },
                { icon: Bell, label: "Gentle reminders", detail: "A nudge before anything you joined" },
                { icon: ShoppingBasket, label: "Shopping runs", detail: "Someone's driving to town? Add to their list" },
                { icon: Users, label: "Meet the house", detail: "Know everyone's name before you arrive" }
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-3.5" data-testid={`app-feature-${i}`}>
                  <div className="w-9 h-9 rounded-full bg-[#F4ECE2] flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4 h-4 text-[#C85A38]" />
                  </div>
                  <div>
                    <p className="text-[#2E2A26] text-sm font-medium">{f.label}</p>
                    <p className="text-[#2E2A26]/45 text-xs leading-relaxed">{f.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center">
            <img src="/images/app-events.jpg" alt="The Savi app — this week's events at the house" loading="lazy" className="w-64 sm:w-72 rounded-[2rem] border border-stone-200 shadow-2xl shadow-[#2E2A26]/15" data-testid="app-showcase-screenshot" />
          </div>
        </div>
      </section>

      {/* ============ 4. ROOMS & PRICES ============ */}
      <PhotoBreak
        name="pool"
        alt="The pool at Savi Coliving seen through the arched veranda"
        eyebrow="Rooms & prices"
        title="Sixteen rooms. Every one with a view."
        testid="photo-break-rooms"
      />
      <section className="py-12 px-4 bg-[#FBF7F1]" id="rooms" data-testid="section-rooms">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-3">Seven room types, sixteen rooms. Pick one, then check the dates.</h2>
            <p className="text-[#2E2A26]/55 text-sm max-w-xl mx-auto">
              From a quiet garden single to a full apartment with its own kitchen. Every rate includes the whole house — workspace, pool, gym, terrace, the weekly programme and the app. Minimum stay one month.
            </p>
          </div>

          <MobileFold id="rooms" title="The seven rooms and prices" teaser={`From ${formatPrice(Math.min(...ROOMS.map((r) => r.price)))} to ${formatPrice(Math.max(...ROOMS.map((r) => r.price)))} a month, excl. IVA. Garden singles to a full apartment.`} cta="See rooms">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {ROOMS.map((room) => (
              <Link
                key={room.id}
                to={`/rooms#${room.id}`}
                className="bg-white rounded-xl overflow-hidden border border-stone-200 hover:border-[#C85A38]/60 hover:shadow-md transition-all flex flex-col group"
                data-testid={`home-room-${room.id}`}
              >
                <div className="relative h-28 overflow-hidden">
                  <img src={room.images[0]} alt={room.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                  {room.badge && <span className="absolute top-2 left-2 bg-[#FBF7F1]/95 text-[#C85A38] text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full">{room.badge}</span>}
                </div>
                <div className="p-4 flex flex-col flex-1">
                <p className="text-[#2E2A26] font-medium mb-0.5">{room.shortName}</p>
                <p className="text-[#2E2A26]/40 text-xs mb-3 leading-snug">{room.note}</p>
                <p className="text-[#C85A38] font-serif text-2xl leading-none">
                  {formatPrice(room.price)}<span className="text-[#2E2A26]/30 text-xs font-sans"> /month</span>
                </p>
                <p className="text-[#2E2A26]/35 text-[11px] mt-1 mb-3">excl. IVA</p>
                <p className={`mt-auto self-start inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2.5 py-1 max-w-full leading-snug ${room.urgent ? "bg-[#C85A38]/10 text-[#C85A38]" : room.available.startsWith("Fully booked") ? "bg-stone-200/70 text-[#2E2A26]/55" : "bg-[#8C9A7C]/15 text-[#4F5C42]"}`} data-testid={`home-room-availability-${room.id}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${room.urgent ? "bg-[#C85A38]" : room.available.startsWith("Fully booked") ? "bg-stone-400" : "bg-[#8C9A7C]"}`} />{room.available}
                </p>
                </div>
              </Link>
            ))}
            <div className="bg-[#2E2A26] rounded-xl p-5 flex flex-col justify-between text-white" data-testid="home-room-cta-card">
              <p className="text-sm leading-relaxed text-white/80">Not sure which? Tell Steve your dates and budget and he&apos;ll suggest the best fit.</p>
              <button onClick={() => scrollTo("dates")} className="mt-4 inline-flex items-center gap-1.5 text-[#C89B7B] text-sm font-medium hover:text-white transition-colors">
                Check dates <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <p className="text-center text-[#2E2A26]/50 text-xs max-w-2xl mx-auto mb-10" data-testid="tax-note">{TAX_NOTE}</p>
          </MobileFold>

          <div className="mt-4 md:mt-0">
          <MobileFold id="included" title="What every rate includes" teaser="Coworking desk, Starlink, pool, gym, sky terrace, yoga, hikes, the weekly programme and the app.">
          <div className="bg-[#F4ECE2] rounded-2xl p-6 sm:p-8 border border-[#E8DAC8] mb-6" data-testid="included-list">
            <h3 className="font-serif text-xl text-[#2E2A26] text-center mb-6">What every rate includes</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { icon: Monitor, label: "Coworking desk", detail: "Dual screens" },
                { icon: Wifi, label: "Starlink WiFi", detail: "Up to 500Mbps" },
                { icon: Waves, label: "Pool" },
                { icon: Mountain, label: "Sky terrace", detail: "200 m²" },
                { icon: Dumbbell, label: "Gym" },
                { icon: Flower2, label: "Yoga & meditation", detail: "Plus sound therapy" },
                { icon: TreePine, label: "Guided hikes", detail: "From the door" },
                { icon: UtensilsCrossed, label: "Full kitchen", detail: "Cook & share" },
                { icon: Users, label: "Weekly programme", detail: "Skill shares & dinners" },
                { icon: PawPrint, label: "Resident dogs" }
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center text-center p-3 bg-white rounded-xl" data-testid={`included-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <item.icon className="w-5 h-5 text-[#C85A38] mb-2" />
                  <p className="text-[#2E2A26] text-xs font-medium">{item.label}</p>
                  {item.detail && <p className="text-[#2E2A26]/40 text-[11px]">{item.detail}</p>}
                </div>
              ))}
            </div>
            <p className="text-center text-[#2E2A26]/40 text-xs mt-5">Utilities, weekly cleaning, fresh linen and the guest app are all in. Anything you&apos;re unsure about — just ask.</p>
          </div>
          </MobileFold>
          </div>

          <div className="bg-white border border-[#C85A38]/25 rounded-2xl p-6 text-center mt-6 md:mt-0" data-testid="garden-lastminute-callout">
            <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase mb-2">Sooner than that?</p>
            <p className="text-[#2E2A26]/60 text-sm max-w-lg mx-auto mb-4">Now and then a two-week window opens in a Garden Room. These never make it to the site — message Steve and ask.</p>
            <a href={`${WHATSAPP}?text=${encodeURIComponent("Hi Steve! Are there any last-minute Garden Room stays available?")}`} target="_blank" rel="noopener noreferrer" data-testid="garden-whatsapp-cta" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb958] text-white rounded-full px-6 py-2.5 text-sm font-medium transition-colors">
              <MessageCircle className="w-4 h-4" /> Ask about a short stay
            </a>
          </div>

          <div className="text-center mt-8">
            <Link to="/rooms">
              <Button variant="outline" className="border-[#2E2A26]/20 text-[#2E2A26] hover:bg-[#2E2A26] hover:text-white rounded-full px-8 py-5 text-sm font-medium">
                See every room, with photos
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ 5. PRACTICAL QUESTIONS ============ */}
      <section className="py-12 px-4 bg-[#F4ECE2]" data-testid="section-practical">
        <div className="max-w-3xl mx-auto">
          <Eyebrow>Practical questions</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] text-center mb-8">The things people ask before they book</h2>
          <div className="space-y-3">
            {[
              { q: "Do I need to be sober to stay?", a: "No. Some guests are years into recovery, many are sober-curious, plenty just don't want alcohol at the centre of their social life. The one thing everyone agrees to: no alcohol or drugs on the property, for the whole stay. Nobody checks up on what you do in town — but most people find they don't miss it." },
              { q: "How does booking actually work?", a: "Send your dates below. Steve replies personally within 24 hours with what's free (or the nearest alternative). If it works for you, a 30% deposit holds the room and the balance is due 30 days before arrival. Then you get your login to the guest app and meet the house before you land." },
              { q: "What's the minimum stay?", a: "One month. Most people stay two or three — that's when the friendships stick. Occasionally a two-week Garden Room window opens; ask Steve." },
              { q: "What's not in the price?", a: "Spanish VAT (IVA) is added to the monthly rate — Steve confirms your exact total in writing before you commit. Food is yours to sort, though most nights someone's cooking for the table." },
              { q: "How do I get there?", a: "Fly into Valencia (VLC). We're about 35 minutes from the airport and 25 from the city centre — a taxi is roughly €45–55, or we can arrange a pickup. Once you've booked, the guest app has step-by-step directions for car, taxi and train." },
              { q: "Is it a retreat? A rehab?", a: "Neither. It's a house where people live and work for a while — with a pool, a proper office and a long dinner table. Steve and Marina live here too. The only rule that makes it different is the one about drinks." }
            ].map((item, i) => (
              <details key={i} className="group bg-white rounded-xl border border-[#E8DAC8] open:border-[#8C9A7C]" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 text-[#2E2A26] text-sm font-medium">
                  {item.q}
                  <span className="text-[#C85A38] text-lg leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="px-5 pb-5 text-[#2E2A26]/60 text-sm leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
          <p className="text-center mt-6">
            <Link to="/faq" className="inline-block py-2 text-[#C85A38] text-sm font-medium hover:underline">All the questions, including cancellations →</Link>
          </p>
        </div>
      </section>

      {/* ============ 6. ENQUIRE ============ */}
      <section id="dates" className="py-14 px-4 bg-[#8C9A7C]/15 scroll-mt-16" data-testid="section-enquire">
        <div className="max-w-3xl mx-auto">
          <WaitlistSignup />
        </div>
      </section>

      <section id="quiz" className="py-12 px-4 bg-[#FBF7F1] scroll-mt-16">
        <div className="max-w-xl mx-auto text-center mb-8">
          <Eyebrow>Not sure yet? · 30 seconds</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-2">Is Savi your kind of place?</h2>
          <p className="text-[#2E2A26]/50 text-sm">Four quick questions, no wrong answers. We&apos;ll tell you honestly whether we think you&apos;d enjoy it here.</p>
        </div>
        <SaviQuiz />
      </section>

      {/* SOBER NOMADS */}
      <section className="py-12 px-4 bg-white" data-testid="sober-nomads-section">
        <div className="max-w-3xl mx-auto grid md:grid-cols-[1.2fr_1fr] gap-8 items-center">
          <a href="https://www.facebook.com/groups/1537243484868294" target="_blank" rel="noopener noreferrer" data-testid="sober-nomads-banner">
            <img src="/images/sober-nomads.jpg" alt="Sober Nomads — coliving, travel and community for sober and sober-curious travellers" loading="lazy" className="w-full rounded-2xl border border-stone-100 shadow-md hover:shadow-lg transition-shadow" />
          </a>
          <div>
            <p className="text-[#8C9A7C] font-label text-xs tracking-[0.2em] uppercase mb-3">Free community · run by Savi</p>
            <h2 className="font-serif text-2xl text-[#2E2A26] mb-3">Not ready to book? Find your people anyway.</h2>
            <p className="text-[#2E2A26]/55 text-sm leading-relaxed mb-5">
              Sober Nomads is our free group for sober and sober-curious remote workers and travellers — alcohol-free destinations, colivings that get it, and honest advice on the road.
            </p>
            <a href="https://www.facebook.com/groups/1537243484868294" target="_blank" rel="noopener noreferrer" data-testid="sober-nomads-cta" className="inline-flex items-center gap-2 bg-[#8C9A7C] hover:bg-[#7C8A6C] text-white rounded-full px-6 py-3 text-sm font-medium transition-colors">
              Join the Facebook group
            </a>
          </div>
        </div>
      </section>

      {/* HIRING, quietly */}
      <div className="px-4 py-5 bg-[#FBF7F1] border-t border-stone-100 text-center" data-testid="home-hiring-strip">
        <Link to="/careers" className="inline-flex items-center gap-2 text-xs text-[#2E2A26]/55 hover:text-[#2E2A26] transition-colors" data-testid="home-hiring-link">
          <span className="font-label text-[11px] tracking-[0.22em] uppercase text-[#C85A38]">We&apos;re hiring</span>
          <span>See the role →</span>
        </Link>
      </div>

      {/* FOOTER */}
      <PublicFooter />
    </div>
  );
};

export default HomePage;
